import {
  CUSTOMER_SUB_ACCOUNT_MAPPINGS_MULTI_SPACE_PARAMETER_SUPPORT,
  CUSTOMER_SUB_ACCOUNT_MAPPINGS_SPACE_PARAMETER_SUPPORT,
} from '@src/components/AppLayout/Panels/Dealer/DealerSpaceParameters';
import { useSpaceParameterControlledFilter } from '@src/components/AppLayout/SpaceParameters';
import { useFeatureFlag, useRoleAuth } from '@src/hooks';
import { useHedgeTradeSubAccountFilters } from '@src/hooks/filters/useHedgeTradeSubAccountFilters';
import { useCustomers, useCustomersByName } from '@src/hooks/useCustomer';
import { useCustomerSecurities, usePricingRules, useSubAccounts } from '@src/providers';
import type { PricingRule } from '@talos/kyoko';
import { ACTION, EMPTY_ARRAY, EntityAdmin, useSecuritiesContext, useSymbolsFilter } from '@talos/kyoko';
import { isNil } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import {
  useRequiredCustomerFilter,
  useRequiredCustomerFilterProperty,
} from '../CustomerPricing/useRequiredCustomerFilter';
import {
  CUSTOMER_PRICING_GROUP_COLUMN_DEF,
  getCustomerPricingDrawerOptionsThroughSubAccounts,
  getPricingRuleEntityDisplayName,
} from '../CustomerPricing/utils';
import { CUSTOMER_SUB_ACCOUNT_MAPPINGS_COLUMNS } from './utils';

export function CustomerSubAccountMappings() {
  const { isAuthorized } = useRoleAuth();
  const { requireCustomerPricingRulesCounterpartyFilter } = useFeatureFlag();

  const { globalDefault, pricingRulesObs, pricingRulesByCounterpartyBySymbol } = usePricingRules();
  const customers = useCustomers();
  const customersByName = useCustomersByName();
  const getEntityDisplayName = useCallback(
    (entity: PricingRule) => getPricingRuleEntityDisplayName(customersByName, entity),
    [customersByName]
  );
  const { allSubAccountBooks } = useSubAccounts();
  const customerSecurities = useCustomerSecurities();
  const { searchableSecurities } = useSecuritiesContext();
  const symbolFilter = useSymbolsFilter();
  const { hedgeSubAccountFilter, tradeSubAccountFilter } = useHedgeTradeSubAccountFilters(allSubAccountBooks);
  const requiredCustomerFilter = useRequiredCustomerFilterProperty(requireCustomerPricingRulesCounterpartyFilter);

  const availableFilterableProperties = useMemo(() => {
    const properties = [requiredCustomerFilter, symbolFilter, hedgeSubAccountFilter, tradeSubAccountFilter];
    // As per the Entity Admin API, undefined is used to indicate a loading state
    return properties.every(prop => !isNil(prop)) ? properties : undefined;
  }, [requiredCustomerFilter, symbolFilter, hedgeSubAccountFilter, tradeSubAccountFilter]);
  const subAccountMappingsSpaceParameterFilter = useSpaceParameterControlledFilter(
    requireCustomerPricingRulesCounterpartyFilter
      ? CUSTOMER_SUB_ACCOUNT_MAPPINGS_SPACE_PARAMETER_SUPPORT
      : CUSTOMER_SUB_ACCOUNT_MAPPINGS_MULTI_SPACE_PARAMETER_SUPPORT,
    availableFilterableProperties
  );
  const {
    requireCustomerPricingRulesCounterpartyFilter: requireLocalCustomerPricingRulesCounterpartyFilter,
    onFilterChanged: syncCounterpartyFilterToProvider,
    hasRequiredCounterpartySelection,
    blotterTableOverlay,
  } = useRequiredCustomerFilter({
    controlledFilter: subAccountMappingsSpaceParameterFilter.controlledFilter,
    controlledCustomerFilterActive: subAccountMappingsSpaceParameterFilter.controlledFilterKeys.length > 0,
    requireCustomerPricingRulesCounterpartyFilter,
  });

  const drawerOptions = useMemo(() => {
    if (!customers || !customerSecurities) {
      return EMPTY_ARRAY;
    }
    return getCustomerPricingDrawerOptionsThroughSubAccounts({
      pricingRulesByCounterpartyBySymbol,
      customers,
      customerSecurities,
      subAccounts: allSubAccountBooks,
      searchableSecurities,
    });
  }, [customers, customerSecurities, allSubAccountBooks, pricingRulesByCounterpartyBySymbol, searchableSecurities]);

  return (
    <EntityAdmin<PricingRule>
      title="Customer SubAccount Mappings"
      subtitle="Configure the trade and hedge subaccount mappings for each customer."
      entityName="SubAccount Mapping"
      path="/pricing-rules"
      dataObservable={pricingRulesObs}
      entityIDField="Counterparty"
      childIDField="Symbol"
      getEntityDisplayName={getEntityDisplayName}
      globalEntity={globalDefault}
      columns={CUSTOMER_SUB_ACCOUNT_MAPPINGS_COLUMNS}
      allowAddEntity={isAuthorized(ACTION.EDIT_DEALER)}
      allowBulkEdit={isAuthorized(ACTION.EDIT_DEALER)}
      allowEditEntity={isAuthorized(ACTION.EDIT_DEALER)}
      persistKey="dealer/customer-sub-account-mappings"
      groupColumnDef={CUSTOMER_PRICING_GROUP_COLUMN_DEF}
      filterableProperties={subAccountMappingsSpaceParameterFilter.filterableProperties}
      externallyControlledFilter={subAccountMappingsSpaceParameterFilter.externallyControlledFilter}
      onFilterChanged={syncCounterpartyFilterToProvider}
      blotterTableHidden={!hasRequiredCounterpartySelection}
      blotterTableOverlay={blotterTableOverlay}
      initialFiltersOpen={requireLocalCustomerPricingRulesCounterpartyFilter}
      initialGroupsExpanded={requireLocalCustomerPricingRulesCounterpartyFilter}
      bulkEditFields={['TradeSubAccount', 'HedgeSubAccount']}
      getDeletePath={entity => {
        if (entity.Symbol != null) {
          return `/pricing-rules/counterparty/${encodeURIComponent(entity.Counterparty)}/security/${encodeURIComponent(
            entity.Symbol
          )}`;
        }
        return `/pricing-rules/counterparty/${encodeURIComponent(entity.Counterparty)}`;
      }}
      getPatchPath={() => '/pricing-rules'}
      // Ignore Tiers responses, and also ignore the default pricing rule response
      userFilterFunc={data => data.Tier == null && data.Counterparty != null}
      drawerOptions={drawerOptions}
      childEntityName="Symbol Override"
      removeNullValuesFromPatchPayload={true}
    />
  );
}
